/**
 * Registro de un paso del circuito. Lo comparten las vistas 7, 8 y 9.
 *
 * **Un paso exacto, no un rango.** El llamante pasa el número concreto que
 * toca. Recuperos documenta el defecto que esto corrige: ofrecer la acción con
 * un `>= 4` seguía mostrando «Registrar entrega» después de haberla
 * registrado, y producía `PASO_DUPLICADO` contra el servidor.
 *
 * `NEG-3`: si la definición del canal exige evidencia para ese paso, el botón
 * no se habilita sin archivo. La regla vive en el catálogo que sirve el
 * backend, así que esta pantalla no la conoce: la consulta.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Textarea,
} from '@mpfn/ui'
import { Callout, DatePicker, FileUploadField, FormField } from '@mpfn/patterns'
import { useRegistrarPaso } from '../api/consultas.js'
import { canalDe, pasoDe, useCatalogos } from '../api/catalogos.js'
import type { Canal } from '../api/tipos.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { hoy } from '../lib/formato.js'

interface Props {
  consultaId: string
  canal: Canal
  /** El paso exacto que corresponde ahora. */
  paso: number
  /**
   * Si ya consta una respuesta no concluyente de la entidad.
   *
   * Decide si el paso de cierre es un trámite o **una puerta de un solo
   * sentido**: sin respuesta registrada, cerrar deja la consulta en «Sin
   * respuesta» y ya no admite una.
   */
  tieneRespuesta?: boolean
  abierto: boolean
  onCambio: (abierto: boolean) => void
  onRegistrado?: () => void
}

export function DialogoPaso({
  consultaId,
  canal,
  paso,
  tieneRespuesta = false,
  abierto,
  onCambio,
  onRegistrado,
}: Props) {
  const catalogos = useCatalogos()
  const registrar = useRegistrarPaso()

  const definicionCanal = canalDe(catalogos, canal)
  const definicionPaso = pasoDe(catalogos, canal, paso)
  // El catálogo manda `null` cuando el paso no exige evidencia.
  const exigeEvidencia = Boolean(definicionPaso?.evidencia)
  /**
   * En qué paso se pide el nº de cargo lo dice el CATÁLOGO (`ARQ-8`).
   *
   * Antes se deducía con `paso === pasoEntrega`, y en el canal físico eso es el
   * 5 mientras que el servidor lo exige en el 1: al registrar el primer paso
   * salía «Indique el número de cargo de Mesa de Partes» sin ningún campo donde
   * escribirlo, y el circuito no se podía empezar.
   */
  const exigeNumeroCargo = definicionCanal?.pasoNumeroCargo === paso

  /**
   * Este paso **cierra la consulta sin respuesta**, y no tiene vuelta atrás.
   *
   * El servidor lo hace así a propósito —registrar el paso de cierre sin que
   * conste respuesta deja el desenlace en `sin_respuesta`, y si era la última
   * consulta viva cierra también el requerimiento—, pero en pantalla el paso
   * se ofrecía **igual que los otros ocho**: un botón secundario más. Quien
   * recorría el circuito de arriba abajo lo pulsaba sin saber que estaba
   * renunciando a registrar la respuesta.
   */
  const cierraSinRespuesta = definicionCanal?.pasoCierre === paso && !tieneRespuesta

  const [fecha, setFecha] = useState(hoy())
  const [observacion, setObservacion] = useState('')
  const [numeroCargo, setNumeroCargo] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)

  useEffect(() => {
    if (!abierto) {
      setFecha(hoy())
      setObservacion('')
      setNumeroCargo('')
      setArchivo(null)
    }
  }, [abierto])

  // El nº de cargo entra en la condición: sin él el servidor rechaza, y dejar
  // pulsar «Registrar» para que vuelva un 422 es hacerle perder el viaje.
  const listo =
    fecha !== '' &&
    (!exigeEvidencia || archivo !== null) &&
    (!exigeNumeroCargo || numeroCargo.trim() !== '')

  return (
    <Dialog open={abierto} onOpenChange={onCambio}>
      <DialogContent>
        <DialogTitle>Registrar el paso {paso}</DialogTitle>
        <DialogDescription>
          {definicionPaso?.descripcion ?? `Paso ${String(paso)} del canal ${canal}.`}
        </DialogDescription>

        {cierraSinRespuesta && (
          <Callout tone="warning" title="Esto cierra la consulta como «Sin respuesta»">
            No consta que la entidad haya respondido. Al registrar este paso la consulta queda
            cerrada y <strong>ya no admite respuesta</strong>; si era la última abierta, cierra
            también el requerimiento.
            <br />
            <strong>Si la entidad respondió, cancele y use «Registrar respuesta» primero.</strong>{' '}
            Para volver a preguntar después de cerrar hay que reiterar el requerimiento.
          </Callout>
        )}

        {exigeEvidencia && (
          <Callout tone="info" title="Este paso exige evidencia">
            Sin el archivo que lo acredita, el paso no se registra.
          </Callout>
        )}

        <form
          className="sigfisc-formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (!listo) return
            registrar.mutate(
              {
                consultaId,
                paso,
                fecha,
                ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
                ...(numeroCargo.trim() ? { numeroCargo: numeroCargo.trim() } : {}),
                ...(archivo ? { archivo } : {}),
              },
              {
                onSuccess: () => {
                  avisarExito(`Paso ${String(paso)} registrado.`)
                  onCambio(false)
                  onRegistrado?.()
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo registrar el paso')
                },
              },
            )
          }}
        >
          <FormField
            id="paso-fecha"
            label="Fecha del hecho"
            required
            hint="No se registran hechos futuros."
          >
            <DatePicker
              id="paso-fecha"
              value={fecha}
              onChange={setFecha}
              yearRange={{ from: Number(hoy().slice(0, 4)) - 1, to: Number(hoy().slice(0, 4)) }}
            />
          </FormField>

          {exigeNumeroCargo && (
            <FormField
              id="paso-cargo"
              label={canal === 'fisico' ? 'N.º de cargo de Mesa de Partes' : 'N.º de trámite'}
              required
              hint="Es el dato con el que se sigue el oficio fuera del sistema."
            >
              <Input
                id="paso-cargo"
                value={numeroCargo}
                onChange={(evento) => {
                  setNumeroCargo(evento.target.value)
                }}
              />
            </FormField>
          )}

          {exigeEvidencia && (
            <FormField id="paso-evidencia" label="Evidencia" required>
              <FileUploadField
                id="paso-evidencia"
                value={archivo}
                onChange={setArchivo}
                accept=".pdf,image/*"
                acceptLabel="PDF o imagen"
                maxSizeMb={20}
                variant="compact"
                onError={(mensaje) => {
                  avisarError(new Error(mensaje), 'El archivo no es válido')
                }}
              />
            </FormField>
          )}

          <FormField id="paso-observacion" label="Observación">
            <Textarea
              rows={2}
              value={observacion}
              onChange={(evento) => {
                setObservacion(evento.target.value)
              }}
            />
          </FormField>

          <div className="sigfisc-dialogo__acciones">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancelar
              </Button>
            </DialogClose>
            {/* `destructive` y con otro texto cuando cierra: el botón tiene
                que anunciar la consecuencia, no el trámite. «Registrar paso 9»
                no dice que se está renunciando a la respuesta. */}
            <Button
              variant={cierraSinRespuesta ? 'destructive' : 'primary'}
              type="submit"
              loading={registrar.isPending}
              disabled={!listo}
            >
              {cierraSinRespuesta ? 'Cerrar sin respuesta' : `Registrar paso ${String(paso)}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
